import { VueReCaptcha } from 'vue-recaptcha-v3'
import type { LayoutSettings, Social, SeoMeta } from '~/models'

type LayoutErrors = {
  settings: string[]
}

export const useLayoutStore = defineStore('layout.store', () => {
  const navigationStore = useNavigationStore()
  const locationStore = useLocationStore()

  const nuxtApp = useNuxtApp()

  const layoutSettings = ref<Undefinable<LayoutSettings>>(undefined)
  const layoutSeoMeta = ref<Undefinable<SeoMeta>>(undefined)
  const errors = ref<LayoutErrors>({
    settings: [],
  })
  const isGlobalErrorShowed = ref<boolean>(false)
  const addFooterSpacing = ref<24 | 32 | 0>(0)

  const { needEasyLoading } = useEasyLoading()
  const layoutIntegrations = computed(() =>
    needEasyLoading.value ? null : layoutSettings.value?.integrations,
  )

  const captchaType = computed(
    () => layoutSettings.value?.settings?.captcha?.type,
  )

  const siteKey = computed(() =>
    layoutSettings.value?.settings?.captcha?.type === 'RECAPTCHA'
      ? layoutSettings.value?.settings?.captcha?.publicKeys?.[3] || ''
      : layoutSettings.value?.settings?.captcha?.publicKeys?.[0] || '',
  )

  const ymAdmiScriptId = computed(
    () =>
      layoutSettings.value?.integrations?.body?.counters?.match(/\d{8}/gm)?.[0],
  )

  const hasWebchat = computed(
    () =>
      locationStore.getHost() !== 'msk' &&
      !!layoutSettings.value?.integrations?.body?.counters_chatbot,
  )

  const hasMangoCallback = computed(
    () => !!layoutSettings.value?.integrations?.body?.counters_callback,
  )

  const socialList = computed(() => layoutSettings.value?.settings?.authSocials)

  const isSocialAuthEmpty = computed(
    () =>
      !(
        Array.isArray(layoutSettings.value?.settings?.authSocials) &&
        !!layoutSettings.value?.settings?.authSocials?.length
      ),
  )

  let hasCaptchaInitialized = false

  const getAuthSocialUrl = (type: Social) => {
    const socialItem = layoutSettings.value?.settings?.authSocials?.find(
      (item) => item.type === type,
    )

    return socialItem?.url
  }

  const vkPixelId = computed(() => {
    const hasScript = layoutSettings.value?.integrations?.body?.vk_ads

    if (hasScript) {
      return layoutSettings.value?.integrations?.body?.vk_ads?.match(
        /\d{7}/gm,
      )?.[0]
    }
  })

  const getLayoutKit = async () => {
    try {
      const layoutKit = await nuxtApp.$api.resources.layout.getLayoutKit()

      if (layoutKit?.data) {
        const { menus, layout, seoMeta } = layoutKit.data
        const { header, footer, mobile, categories } = menus

        navigationStore.setFooterMenuNav(footer)
        navigationStore.setHeaderMenu(categories[0]?.items)
        navigationStore.setHeaderMenuExtended(header[0]?.items)
        navigationStore.setMobileMenu(mobile[0]?.items)
        layoutSettings.value = layout
        layoutSeoMeta.value = seoMeta
      }
    } catch (error) {
      errors.value.settings.push(StringifyError(error))
    }
  }

  const setGlobalError = (toggler: boolean) => {
    isGlobalErrorShowed.value = toggler
  }

  watch(
    () => layoutSettings.value,
    () => {
      if (process.client) {
        if (hasCaptchaInitialized) return
        initCaptcha(captchaType.value)
      }
    },
  )

  const initCaptcha = (
    captchaType: 'SMART_CAPTCHA' | 'RECAPTCHA' | undefined,
  ) => {
    switch (captchaType) {
      case 'SMART_CAPTCHA':
        initSmartCaptcha()
        break
      case 'RECAPTCHA':
        initReCaptcha(siteKey.value)
        break
    }
  }

  const initSmartCaptcha = () => {
    const script = document.createElement('script')

    script.src = 'https://smartcaptcha.yandexcloud.net/captcha.js'
    script.defer = true
    document.head.appendChild(script)

    const div = document.createElement('div')

    div.id = 'smart-captcha'
    document.body.appendChild(div)

    hasCaptchaInitialized = true
  }

  const initReCaptcha = (siteKey: string) => {
    nuxtApp.vueApp.use(VueReCaptcha, {
      siteKey,
      loaderOptions: {
        autoHideBadge: true,
      },
    })

    hasCaptchaInitialized = true
  }

  return {
    layoutSettings,
    layoutSeoMeta,
    errors,
    isGlobalErrorShowed,
    layoutIntegrations,
    getLayoutKit,
    setGlobalError,
    ymAdmiScriptId,
    isSocialAuthEmpty,
    hasWebchat,
    hasMangoCallback,
    vkPixelId,
    addFooterSpacing,
    getAuthSocialUrl,
    socialList,
    siteKey,
    captchaType,
  }
})
